import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Controller, UseFormReturn } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { FormControl, InputLabel, TextField } from '@mui/material'
import Autocomplete, { AutocompleteChangeReason } from '@mui/material/Autocomplete'

interface Props {
  name: string
  label?: JSX.Element | string
  form: UseFormReturn<any>
  options: SelectOption[]
  initialValue?: SelectOption[]
  status: RequestStatus
  onRemove?: (arg0: string | number) => void
  confirmRemoveMessage?: string
  disabled?: boolean
  singleValue?: boolean
  clearOptions?: () => void
  search?: (searchString: string) => void
  readOnly?: boolean
}

export default function SearchableSelect({
  name,
  label,
  form,
  options,
  initialValue,
  status,
  onRemove,
  confirmRemoveMessage,
  disabled = false,
  singleValue = false,
  clearOptions,
  search,
  readOnly = false,
}: Props): JSX.Element {
  const [values, setValues] = useState<SelectOption[]>(initialValue || [])
  const [originalValue] = useState<SelectOption[]>(initialValue || [])
  const [namesAdded, setNamesAdded] = useState<boolean>(false)
  const { control, setValue } = form
  const intl = useIntl()
  const [previousOptions, setPreviousOptions] = useState<SelectOption[]>([])

  const handleChange = (newValues: SelectOption[]) => {
    if (singleValue) {
      const newValue = newValues.filter((v: SelectOption) => !values.includes(v))
      setValues(newValue)
    } else {
      setValues(newValues)
    }
    return newValues
  }

  const handleRemove = (option: SelectOption) => {
    if (confirmRemoveMessage && window.confirm(confirmRemoveMessage)) {
      doRemove(option)
    } else if (!confirmRemoveMessage) {
      doRemove(option)
    }
  }

  const doRemove = (option: SelectOption) => {
    const newValues = values.filter((value) => option !== value)
    setValues(newValues)
    setValue(name, newValues)
    if (onRemove) {
      onRemove(option.key)
    }
  }

  // This adds correct labels to id:s received from backend
  useEffect(() => {
    if (options.length > 0 && initialValue && initialValue !== originalValue && !namesAdded) {
      const isRealNames = !JSON.stringify(initialValue).includes('...')
      if (isRealNames) {
        setValues(initialValue)
        setNamesAdded(true)
      }
    }
  }, [options, initialValue, originalValue, namesAdded])

  // This removes removed item's children
  useEffect(() => {
    if (options.length !== previousOptions.length && previousOptions.length > 0) {
      setPreviousOptions(options)
      const newValues = values.filter((v) => options.some((o) => o.key === v.key))
      setValues(newValues)
      setValue(name, newValues)
    }
  }, [options, previousOptions, values, name, setValue])

  useEffect(() => {
    if (readOnly) {
      return
    }
    if (disabled && values.length > 0) {
      setValues([])
      if (name === 'locationCourses' && clearOptions) {
        clearOptions()
      }
    }
  }, [disabled, values, clearOptions, name])

  useEffect(() => {
    if (initialValue && initialValue.length > 0 && values.length === 0) {
      setValues(initialValue)
    }
  }, [initialValue, values])

  const searchRequest = (searchString: string | undefined) => {
    if (search && searchString) {
      search(searchString)
    }
  }

  const getNoOptionsText = () => {
    return search
      ? intl.formatMessage({ id: 'strings.typeMin3Characters' })
      : intl.formatMessage({ id: 'strings.noOptions' })
  }

  return (
    <>
      <InputContainer margin="normal" fullWidth disabled={disabled}>
        {label && <FieldLabel>{label}</FieldLabel>}
        <Controller
          control={control}
          name={name}
          defaultValue={values}
          render={({ field: { onChange } }) => (
            <Autocomplete
              multiple
              loading={status === 'loading'}
              disableListWrap
              onChange={(event, newValues, reason: AutocompleteChangeReason) => {
                if (reason !== 'removeOption') {
                  onChange(handleChange(newValues))
                }
              }}
              onInputChange={(event: any) => searchRequest(event?.target?.value)}
              options={options}
              getOptionLabel={(option) => option.label}
              filterSelectedOptions
              autoHighlight
              value={values}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" placeholder={intl.formatMessage({ id: 'strings.search' })} />
              )}
              isOptionEqualToValue={(option, value) => option.key === value.key}
              disabled={disabled}
              noOptionsText={getNoOptionsText()}
              sx={{
                '& .MuiAutocomplete-inputRoot': {
                  display: 'flex',
                  padding: 'var(--p-25, 10px) var(--p-3, 12px)',
                  alignItems: 'center',
                  gap: '8px',
                  alignSelf: 'stretch',
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',

                  '& fieldset': {
                    borderColor: '#e4e4e7',
                  },

                  '&:hover fieldset': {
                    borderColor: '#e4e4e7',
                  },

                  '&.Mui-focused fieldset': {
                    borderColor: '#e4e4e7',
                  },

                  '& .MuiAutocomplete-input': {
                    padding: 0,
                  },
                },
                '& .MuiAutocomplete-popupIndicator': {
                  color: '#8A8A8E',
                },
              }}
            />
          )}
        />
      </InputContainer>
      <ValueContainer>
        {values.map((option) => (
          <div key={option.key} data-index={option.key}>
            <span>{option.label}</span>
            {!readOnly && (
              <span className="delete" onClick={() => handleRemove(option)}>
                x
              </span>
            )}
          </div>
        ))}
      </ValueContainer>
    </>
  )
}

const InputContainer = styled(FormControl)`
  margin: 20px 0;

  & .MuiChip-root,
  .MuiAutocomplete-clearIndicator,
  .MuiAutocomplete-popupIndicator {
    display: none;
  }

  & .MuiAutocomplete-inputRoot {
    padding: 10px !important;
  }
`

const FieldLabel = styled(InputLabel)`
  margin-top: -20px;
  transition: none;
  transform: none;
`

const ValueContainer = styled.div`
  padding: 20px;
  background-color: ${(props) => props.theme.customPalette.lightGray};
  border: 1px solid;
  border-color: ${(props) => props.theme.customPalette.mediumGray};
  color: ${(props) => props.theme.palette.primary.main};
  font-size: 1.25em;
  min-height: 242px;
  border-radius: 5px;

  & > div {
    margin-bottom: 10px;

    & > span.delete {
      margin-left: 20px;
      cursor: pointer;
      color: ${(props) => props.theme.customPalette.error};
    }
  }
`
